// src/components/ui/Button/index.tsx
import { DateTime } from "luxon";
import "./DatePicker.scss";
import clsx from "clsx";
import { useEffect, useRef } from "react";

type DatePickerProps = {
  label: string;
  value?: Date | null;
  maxValue?: Date;
  minValue?: Date;
  className?: string;
  disabled?: boolean;
  onClick?: () => void;
  onChange: (date: Date) => void;
  error?: string;
  required?: boolean;
  id?: string;
  autoComplete?: "off";
};

export const DatePicker = ({
  label,
  value,
  maxValue,
  minValue,
  className,
  disabled,
  onClick,
  onChange,
  error,
  required = false,
  id,
  autoComplete = "off",
}: DatePickerProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const inputId =
    id || `datepicker-${label.toLowerCase().replace(/\s+/g, "-")}`;

  useEffect(() => {
    if (error && inputRef.current) {
      inputRef.current.setAttribute("aria-invalid", "true");
      inputRef.current.setAttribute("aria-describedby", `${inputId}-error`);
    } else if (inputRef.current) {
      inputRef.current.setAttribute("aria-invalid", "false");
      inputRef.current.removeAttribute("aria-describedby");
    }
  }, [error, inputId]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !disabled) {
      onClick?.();
    }
  };

  const formattedValue = value
    ? DateTime.fromJSDate(value).toFormat("yyyy-MM-dd")
    : "";

  return (
    <div
      className={clsx("datepicker-container", className)}
      role="group"
      aria-labelledby={`${inputId}-label`}
      aria-describedby={error ? `${inputId}-error` : undefined}
    >
      <label
        id={`${inputId}-label`}
        htmlFor={inputId}
        className="datepicker-label fs-7"
      >
        {label}
        {required && (
          <span className="text-error ml-1" aria-hidden="true">
            *
          </span>
        )}
      </label>
      <input
        ref={inputRef}
        id={inputId}
        type="date"
        className="datepicker-input fs-6"
        disabled={disabled}
        value={formattedValue}
        max={
          maxValue
            ? DateTime.fromJSDate(maxValue).toFormat("yyyy-MM-dd")
            : undefined
        }
        min={
          minValue
            ? DateTime.fromJSDate(minValue).toFormat("yyyy-MM-dd")
            : undefined
        }
        onClick={() => onClick?.()}
        onInput={(e) => {
          const input = e.currentTarget;
          const year = input.value.split("-")[0];
        
          if (year && year.length > 4) {
            input.value = formattedValue;
          }
        }}
        onChange={(e) => {
          const newDate = new Date(e.target.value);
          if (!isNaN(newDate.getTime())) {
            onChange(newDate);
          }
        }}
        onKeyDown={handleKeyDown}
        aria-required={required}
        aria-disabled={disabled}
        aria-label={label}
        aria-invalid={!!error}
        aria-describedby={error ? `${inputId}-error` : undefined}
        autoComplete={autoComplete}
      />
      {error && (
        <div
          id={`${inputId}-error`}
          className="datepicker-error"
          role="alert"
          aria-live="polite"
        >
          {error}
        </div>
      )}
    </div>
  );
};
